import { useMutation } from '@tanstack/react-query';
import confetti from 'canvas-confetti';
import { useEffect, useMemo, useRef } from 'react';
import { Navigate } from 'react-router';
import { NoEvaluationNotice } from 'src/components/ui/no-evaluation-notice.tsx';
import { SECTIONS, sectionQuestions } from 'src/data/sections.ts';
import apiClient from 'src/lib/apiClient.ts';
import {
  avgAnswered,
  getResultType,
  shortSectionTitle,
  subgroupScores,
  truncateToDisplay
} from 'src/lib/scoring.ts';
import { useCheck } from 'src/state/check-context.ts';

import { ResultPageContent } from './result-page-content.tsx';

export function ResultPage() {
  const { answers, reset, lead, context, arcId } = useCheck();

  // score undefined = Sektion ohne substanzielle Antworten („keine Angabe").
  const sectionScores = useMemo(
    () =>
      SECTIONS.map((s) => {
        const score = avgAnswered(sectionQuestions(s).map((q) => answers[q.id]));
        return {
          id: s.id,
          title: s.title,
          score: score !== undefined ? truncateToDisplay(score) : undefined
        };
      }),
    [answers]
  );

  const levels = SECTIONS.map((s, i) => ({
    id: s.id,
    name: shortSectionTitle(s),
    score: sectionScores[i].score,
    tone: getResultType(sectionScores[i].score ?? 1).tone,
    Icon: s.icon,
    breakdown: s.id === 'wirkungsebenen' ? subgroupScores(s, answers) : undefined
  }));

  // undefined = keine einzige substanzielle Antwort → keine Einstufung möglich
  // (statt fälschlich Score 0,0 / „Customer-Blind" anzuzeigen und zu persistieren).
  const overallRaw = avgAnswered(Object.values(answers));
  const overall = overallRaw !== undefined ? truncateToDisplay(overallRaw) : undefined;
  const resultType = overall !== undefined ? getResultType(overall) : undefined;

  // Celebrate the top maturity tier once, in zeb brand colors. Skipped for
  // empty state and for users who prefer reduced motion.
  const firedConfetti = useRef(false);
  useEffect(() => {
    if (firedConfetti.current) return;
    if (resultType?.tone !== 'green') return;
    if (Object.keys(answers).length === 0) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    firedConfetti.current = true;
    confetti({
      particleCount: 70,
      spread: 64,
      startVelocity: 38,
      origin: { y: 0.3 },
      colors: ['#009ED6', '#001A37', '#00A0D7'],
      disableForReducedMotion: true
    });
  }, [resultType?.tone, answers]);

  // Persistiertes Format bleibt numerisch (Backend-Kompatibilität): Sektionen
  // ohne Antworten als 0-Sentinel — auf der 1–5-Skala eindeutig „keine Daten".
  const resultPayload = useMemo(
    () => ({
      context,
      answers,
      sectionScores: sectionScores.map((s) => ({ ...s, score: s.score ?? 0 })),
      totalScore: overall ?? 0,
      arcId
    }),
    [context, answers, sectionScores, overall, arcId]
  );

  const hasPersisted = useRef(false);

  const { mutate: persistResult } = useMutation({
    mutationFn: (payload: typeof resultPayload) => apiClient.post('/results', payload)
  });

  useEffect(() => {
    if (hasPersisted.current) return;
    if (Object.keys(answers).length === 0) return;
    // Ohne substanzielle Antworten gibt es nichts zu benchmarken → nicht persistieren.
    if (overall === undefined) return;
    hasPersisted.current = true;
    persistResult(resultPayload);
  }, [answers, overall, resultPayload, persistResult]);

  // Hooks above must run unconditionally (rules-of-hooks); guards come after.
  if (Object.keys(answers).length === 0) return <Navigate to="/" replace />;
  // reset() leert die Antworten → der Guard darüber leitet zum Start zurück.
  if (overall === undefined || resultType === undefined) {
    return <NoEvaluationNotice onRestart={reset} />;
  }

  return (
    <ResultPageContent
      overall={overall}
      resultType={resultType}
      levels={levels}
      lead={lead}
      answers={answers}
      context={context}
      onReset={reset}
    />
  );
}
